import { createElement } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";

import { AccessProvider } from "./access/AccessProvider";
import { OperatorsAuthProvider } from "./operatorsAuth";
import { routeTree } from "./routeTree.gen";
import { StoreProvider } from "./store";
import {
  AccountManagementPipelinePreview,
  isAccountManagementPipelinePreview,
} from "./tools/dashboards/accountManagementPipelinePreview";

type RouterHistory = NonNullable<Parameters<typeof createRouter>[0]["history"]>;

export function getRouter(history: RouterHistory) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60_000,
        gcTime: 30 * 60_000,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });

  return createRouter({
    routeTree,
    history,
    context: {
      queryClient,
    },
    Wrap: ({ children }) => {
      if (typeof window !== "undefined" && isAccountManagementPipelinePreview(window.location)) {
        return createElement(AccountManagementPipelinePreview);
      }
      return createElement(
        QueryClientProvider,
        { client: queryClient },
        createElement(
          StoreProvider,
          null,
          createElement(OperatorsAuthProvider, null, createElement(AccessProvider, null, children)),
        ),
      );
    },
  });
}

export type AppRouter = ReturnType<typeof getRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
